import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import Editor from '@monaco-editor/react';

const AmendVersionModal = ({ version, onClose, onSave }) => {
  const [editContent, setEditContent] = useState('');
  const [changeMessage, setChangeMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (version) {
      setEditContent(version.content);
      setChangeMessage(version.change_message || '');
    }
  }, [version]);

  const handleSave = async () => {
    if (!editContent.trim()) return;

    setLoading(true);
    try {
      await onSave({
        content: editContent,
        changeMessage: changeMessage || undefined
      });
      onClose();
    } catch (error) {
      console.error('Failed to amend version:', error);
      // You might want to show an error message here
    } finally {
      setLoading(false);
    }
  };

  if (!version) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Amend Version v{version.version_number}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Edit this version without creating a new one
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 p-6 overflow-hidden flex flex-col">
          {/* Change Message */}
          <div className="mb-4">
            <label htmlFor="amendChangeMessage" className="block text-sm font-medium text-gray-700 mb-1">
              Update change description (optional)
            </label>
            <input
              type="text"
              id="amendChangeMessage"
              value={changeMessage}
              onChange={(e) => setChangeMessage(e.target.value)}
              className="input"
              placeholder="e.g., Fixed typos and improved clarity"
            />
          </div>

          {/* Content Editor */}
          <div className="flex-1 min-h-0">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content
            </label>
            <div className="border border-gray-300 rounded-lg overflow-hidden h-full">
              <Editor
                height="400px"
                defaultLanguage="text"
                value={editContent}
                onChange={(value) => setEditContent(value || '')}
                options={{
                  minimap: { enabled: false },
                  lineNumbers: 'on',
                  wordWrap: 'on',
                  scrollBeyondLastLine: false,
                  fontSize: 14,
                  fontFamily: 'ui-monospace, SFMono-Regular, Monaco, Consolas, Liberation Mono, Courier New, monospace',
                  theme: 'vs',
                }}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center p-6 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            <strong>Note:</strong> This will modify the existing version directly. 
            Use "Duplicate Version" if you want to keep the original unchanged.
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="btn-primary"
              disabled={!editContent.trim() || loading}
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmendVersionModal; 